import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  Typography,
} from "@mui/material";
import { useCallback, useRef, useState } from "react";

import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  MoreHoriz,
  PlayArrow,
} from "@mui/icons-material";
import Slider from "react-slick";

import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import PlayBtn from "../../../SVGs/PlayBtn";
import CustomCircularProgress from "../../../components/CustomCircularProgress";
import { listVideosService } from "../../../services/video";
import { getDate } from "../../../shared/getDate";

const settings = {
  infinite: false,
  dots: false,
  swipeToSlide: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  // nextArrow: <SampleNextArrow />,
  // prevArrow: <SamplePrevArrow />,
};
let starringData = [
  {
    image: "/images/mona.png",
    name: "Mona",
  },
  {
    image: "/images/yara.png",
    name: "Yara",
  },
  {
    image: "/images/nanci.png",
    name: "Nanci",
  },
  {
    image: "/images/soha.png",
    name: "Soha",
  },
  {
    image: "/images/shourok.png",
    name: "Shourok",
  },
];

export default function VideoPlayer() {
  const nav = useNavigate();
  const sliderRef = useRef(null);

  const [duration, setDuration] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverData, setPopoverData] = useState({
    name: "",
    imageSrc: "",
  });

  const handleVideoPlay = useCallback(
    (id) => nav(`/video/${id ? id : 0}`),
    [nav]
  );

  const handleIconButtonClick = (name, event) => {
    setPopoverData({
      name: name,
    });
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "popover" : undefined;

  const { isPending, data } = useQuery({
    queryKey: ["data"],
    queryFn: () => listVideosService({}),
  });

  return (
    <Box sx={{ width: 1 }}>
      {isPending ? (
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            width: 1,
            height: "700px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Slider
          ref={sliderRef}
          {...{ ...settings, infinite: data?.data?.length > 1 }}
        >
          {data?.data?.map((video) => (
            <Box>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "654px",
                  overflow: "hidden",
                }}
              >
                <video
                  src={video?.media}
                  poster={video?.poster}
                  onLoadedMetadata={(e) => setDuration(e?.target?.duration)}
                  preload="metadata"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center",
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    left: "0",
                    right: "0",
                    top: "0",
                    bottom: "0",
                    position: "absolute",
                    justifyContent: "space-between",
                    alignItems: "center",
                    "& > *": {
                      color: "#ffffff",
                    },
                  }}
                >
                  <IconButton
                    onClick={() =>
                      sliderRef.current && sliderRef.current.slickPrev()
                    }
                  >
                    <KeyboardArrowLeft
                      sx={{
                        color: "#ffffff",
                        fontSize: { xs: "1.5em", sm: "2em" },
                        transition: "0.5s all",
                        "&: hover": {
                          color: "#EA5069",
                          bgcolor: "transparent",
                        },
                        zIndex: 10,
                      }}
                    />
                  </IconButton>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-evenly",
                      alignItems: "center",
                      width: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: { xs: "none", sm: "flex" },
                        flexDirection: "column",
                        zIndex: "5",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Typography
                        sx={{
                          pointerEvents: "none",
                          color: "#ffffff",
                          fontSize: {
                            xs: "10",
                            sm: "12px",
                            md: "15px",
                            lg: "20px",
                            xl: "30px",
                          },
                        }}
                      >
                        TV Service
                      </Typography>

                      <Typography
                        sx={{
                          color: "#ffffff",
                          fontSize: {
                            xs: "15",
                            sm: "20px",
                            md: "30px",
                            lg: "40px",
                            xl: "60px",
                          },
                          pointerEvents: "none",
                          maxWidth: "708px",
                        }}
                      >
                        {video?.name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1em",
                          color: "text.secondary",
                          pointerEvents: "none",
                        }}
                      >
                        {video?.releaseDate && (
                          <>
                            <Typography variant="subtitle2">
                              {getDate(
                                video?.releaseDate,
                                undefined,
                                "DD MMM, YYYY"
                              )}
                            </Typography>
                            <Box
                              sx={{
                                backgroundColor: "text.secondary",
                                width: "1px",
                                height: "20px",
                              }}
                            />
                          </>
                        )}
                        {duration && (
                          <>
                            <Typography variant="subtitle2">
                              {duration < 60
                                ? `${Math.ceil(duration)} sec`
                                : `${Math.ceil(duration / 60)} min`}
                            </Typography>
                            <Box
                              sx={{
                                backgroundColor: "text.secondary",
                                width: "1px",
                                height: "20px",
                              }}
                            />
                          </>
                        )}

                        {video?.categories?.length > 0 && (
                          <Typography variant="subtitle2">
                            {video?.categories[0]?.category?.name}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        sx={{
                          color: "text.secondary",
                          my: 2,
                          maxWidth: "686px",
                        }}
                        variant="h6"
                      >
                        {video?.description}
                      </Typography>

                      <Button
                        variant="contained"
                        sx={{
                          boxShadow: "0px 7px 15px #FFC02A",
                          transition: "0.5s all",
                          mb: 2,
                        }}
                        onClick={() => handleVideoPlay(video?.postId)}
                        startIcon={<PlayArrow />}
                      >
                        <Typography variant="h6">Watch Now</Typography>
                      </Button>

                      <Box>
                        <Typography sx={{ mb: 2 }}>Cast</Typography>
                        {starringData?.map((item, index) => (
                          <IconButton
                            key={index}
                            onClick={(event) =>
                              handleIconButtonClick(item.name, event)
                            }
                            sx={{
                              position: "relative",
                              p: 0,
                              left: `-${10 * index}px`,
                              zIndex: index,
                            }}
                          >
                            <Avatar src={item.image} />
                          </IconButton>
                        ))}
                      </Box>

                      <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handlePopoverClose}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "center",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "center",
                        }}
                      >
                        <Box
                          sx={{
                            padding: { xs: "0.5em", sm: "1em" },
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: { xs: "0.5em", sm: "1em" },
                          }}
                        >
                          <Typography>{popoverData.name}</Typography>
                          <Button
                            sx={{
                              backgroundColor: "#FBBF14",
                              color: "#273238",
                              fontFamily: "RobotoMedium",
                              fontSize: "1rem",
                              height: "2em",
                              "&: hover": {
                                color: "#FBBF14",
                                backgroundColor: "#273238",
                              },
                            }}
                          >
                            IMDb
                          </Button>
                        </Box>
                      </Popover>
                    </Box>
                    <Box
                      sx={{
                        zIndex: 10,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <IconButton
                        onClick={() => handleVideoPlay(video?.postId)}
                        sx={{
                          fill: "#f9f9f9",
                          stroke: "#fff",
                          transition: "0.5s all",
                          "&: hover": {
                            fill: "#EA5069",
                            stroke: "#EA5069",
                            bgcolor: "transparent",
                          },
                          width: {
                            xs: "60px",
                            sm: "70px",
                            md: "80px",
                            lg: "90px",
                            xl: "110px",
                          },
                        }}
                      >
                        <PlayBtn />
                      </IconButton>
                    </Box>
                  </Box>

                  <IconButton
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      zIndex: 10,
                    }}
                    onClick={() =>
                      sliderRef.current && sliderRef.current.slickNext()
                    }
                  >
                    <KeyboardArrowRight
                      sx={{
                        color: "#ffffff",
                        fontSize: { xs: "1.5em", sm: "2em" },
                        transition: "0.5s all",
                        "&: hover": {
                          color: "#EA5069",
                          bgcolor: "transparent",
                        },
                      }}
                    />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 1,
                    pointerEvents: "none",
                  }}
                >
                  <img
                    src="/images/homeVideoBgd.svg"
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    padding: { sm: "1em", md: "2em", lg: "3em" },
                    display: { xs: "none", sm: "block" },
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: { xs: "flex", sm: "none" },
                  flexDirection: "column",
                  gap: "1em",
                  padding: "1em",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      pointerEvents: "none",
                    }}
                  >
                    TV Service
                  </Typography>
                  <CustomCircularProgress
                    value={86}
                    color={"green"}
                    size={40}
                    valueSize={"15px"}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: "1em",
                    pointerEvents: "none",
                    "& button": {
                      backgroundColor: "#A9A9A9",
                      fontSize: "12px",
                      color: "white",
                    },
                  }}
                >
                  <Button size="small">PG-13</Button>
                  <Button size="small">HD</Button>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    pointerEvents: "none",
                  }}
                >
                  Seven Days in Ibiza
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: "1em",
                    pointerEvents: "none",
                  }}
                >
                  <Typography variant="caption">
                    15 August, 2016 (UK)
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: "black",
                      width: "1px",
                    }}
                  />
                  <Typography variant="caption">163 min</Typography>
                  <Box
                    sx={{
                      backgroundColor: "black",
                      width: "1px",
                    }}
                  />
                  <Typography variant="caption">Documentary</Typography>
                </Box>
                <Typography variant="body1">
                  A sex columnist, Carrie Bradshaw, and her three friends
                  Samantha, Charlotte and Miranda explore Manhattan's dating
                  scene, chronicling the mating habits of single New Yorkers.
                </Typography>
                <Box
                  sx={{
                    display: { xs: "flex" },
                    gap: "1em",
                  }}
                >
                  <Button
                    sx={{
                      backgroundColor: "#EA5069",
                      boxShadow: "0px 7px 15px #EA506940",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      color: "#ffffff",
                      gap: "0.5em",
                      textTransform: "none",
                      fontFamily: "RobotoMedium",
                      fontSize: "1rem",
                      transition: "0.5s all",
                      "&:hover": {
                        backgroundColor: "white",
                        color: "#EA5069",
                      },
                    }}
                    onClick={() => handleVideoPlay(video?.postId)}
                  >
                    <PlayArrow />
                    Watch Now
                  </Button>
                  <IconButton
                    sx={{
                      transition: "0.5s all",
                      outline: "2px solid #ffffff",
                      color: "#ffffff",
                      "&:hover": {
                        color: "#EA5069",
                        outline: "2px solid #EA5069",
                      },
                    }}
                  >
                    <MoreHoriz />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    flexDirection: "column",
                  }}
                >
                  <Typography sx={{ textTransform: "uppercase" }}>
                    STARRING
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    {starringData?.map((item, index) => (
                      <IconButton
                        key={index}
                        onClick={(event) =>
                          handleIconButtonClick(item.name, event)
                        }
                      >
                        <Avatar src={item.image} />
                      </IconButton>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Slider>
      )}
    </Box>
  );
}
